import { useContext, useEffect, useMemo, useState } from 'react';
import { Modal } from 'react-bootstrap';
import { getBusinessPartnerStatus, getNewLoadQaComplianceChecklist, putNeeNor } from '../../apiCalls';
import BusinessPartnerCard from '../../BusinessPartners/BusinessPartnerCard';
import SimpleTable from '../../SimpleTable';
import { StoreContext } from '../../Store';
import ComplianceChecklistModal from './modals/ComplianceChecklistModal';
import ConfirmActionModal from '../../ConfirmActionModal';
import MultipleConsignorsModal from '../MultipleConsignorsModal/MultipleConsignorsModal';
import { QAStoreContext } from './QAStore';
import {
  checkIfConsigneeIsValid,
  checkIfConsignorIsValid,
  checkIfCustomerIsValid,
  fetchRevenuePayableData,
  formatCompliancePersistedData,
} from './utils';
import { VatTypeEnum } from '../../../utils/enums';

const modalTypes = {
  invoiceCustomer: 'invoiceCustomer',
  consignor: 'consignor',
  consignee: 'consignee',
};

const ComplianceChecklistTable = ({
  setIsComplianceChecklistValid,
  setShowPreferenceCheckModal,
  displayCheckModal,
  readOnly,
  persistedData,
  config,
  incoterm,
  isPreferenceCheckValid,
  typeErrorMessage,
}) => {
  // 3. State Hooks
  const [showKebabModal, setShowKebabModal] = useState(false);
  const [showMultipleConsignorsModal, setShowMultipleConsignorsModal] = useState(false);
  const [selectedBusinessPartner, setSelectedBusinessPartner] = useState(null);
  const [showMultipleConsignorsWarning, setShowMultipleConsignorsWarning] = useState(false);

  // 4. Store values
  const store = useContext(StoreContext);
  const [companyId] = store.clientID;
  const [companyName] = store.clientIDname;
  const [representative] = store.representative;
  const [productList] = store.QAProductsList;
  const setCustomsMoPConfirmation = store.customsMoPConfirmation[1];

  const invoiceLines = productList?.map((r) => ({ invNumber: r.invoiceNumber, consignorId: r.consignorId }));

  const [consigneeStatus, setConsigneeStatus] = store.QAConsigneeData;
  const [consignorStatus, setConsignorStatus] = store.QAConsignorData;
  const [customerStatus, setCustomerStatus] = store.QACustomerData;
  const [controlledGoodsType] = store.controlledGoods;
  const [multipleConsignors] = store.multipleConsignors;
  const [consignorDetails] = store.multipleConsignorDetails;

  const [isMultipleInvoicesValid] = store.isMultipleInvoicesValid;
  const [isMultipleConsignorsValid] = store.isMultipleConsignorsValid;

  const qaStore = useContext(QAStoreContext);
  const [consigneeData, setConsigneeData] = qaStore.consigneeData;
  const [consignorData, setConsignorData] = qaStore.consignorData;
  const setNewLoadQaVat = qaStore.newLoadQaVat[1];

  const [modalData, setModalData] = useState({});

  // 5. Derived Data / Memoized Values
  const { isInvoiceCustomerValid, isConsigneeValid, isConsignorBPValid } = useMemo(() => {
    const isInvoiceCustomerValid = checkIfCustomerIsValid(customerStatus);
    const isConsigneeValid = checkIfConsigneeIsValid(consigneeStatus, customerStatus, representative);
    const isConsignorBPValid = checkIfConsignorIsValid(
      config,
      incoterm,
      consignorStatus,
      customerStatus,
      representative
    );

    return { isInvoiceCustomerValid, isConsigneeValid, isConsignorBPValid };
  }, [customerStatus, config, consigneeStatus, incoterm, consignorStatus, representative]);

  const controlledGoodsTypeSelected = useMemo(() => {
    return controlledGoodsType === 'false' ? false : !!controlledGoodsType;
  }, [controlledGoodsType]);

  const controlledGoodsTypeValue = useMemo(() => {
    return !controlledGoodsTypeSelected ? '' : controlledGoodsType === 'Y' ? 'Controlled' : 'Non-Controlled';
  }, [controlledGoodsTypeSelected, controlledGoodsType]);

  const showExWorksForConsignorBp = useMemo(() => incoterm === 'EXW', [incoterm]);

  const getBusinessPartnerDetails = async (status, type, incoterm = '') => {
    const { id, companyName, eori } = status;
    if (!status || !id) {
      return;
    }

    try {
      const data = await getNewLoadQaComplianceChecklist(id, incoterm);
      let bpData = { companyName, ...data };

      if (readOnly && persistedData.length) {
        const persistedBusinessPartnerData = formatCompliancePersistedData(persistedData[0], type, companyName);
        let nextStatus = { ...persistedBusinessPartnerData };
        if (!nextStatus.id) {
          try {
            const live = await getNewLoadQaComplianceChecklist(id, incoterm);
            if (live?.id) {
              nextStatus = { ...nextStatus, id: live.id };
            }
          } catch {
            /* keep persisted-only row; View stays off without a BP id */
          }
        }
        bpData = { ...bpData, ...nextStatus };
      }

      return bpData;
    } catch {
      return {
        companyName,
        cabieStatus: '',
        cabieStatusValid: false, //if unable to fetch data, cabieStatusValid is false
        consignee: '',
        dbStatus: '',
        dbStatusValid: false, //if unable to fetch data, dbStatusValid is false
        eori: eori,
        id: null,
        standingAuthority: '',
        standingAuthorityValid: true,
      };
    }
  };

  const saveNeenorData = async (oldData, newData, neenor) => {
    const updatedData = { ...oldData, ...newData };

    try {
      const res = await putNeeNor(updatedData);
      if (res[0].completed === 'failed') {
        throw new Error('Unable to update data');
      }
      if (neenor === 'Consignee') {
        setConsigneeData(updatedData);
      }
      if (neenor === 'Consignor') {
        setConsignorData(updatedData);
      }
      const modalDataToDisplay = getNeeNorModalDetails(neenor, updatedData);

      setModalData(modalDataToDisplay);
    } catch (error) {
      console.error(error);
    }
  };

  const refreshBusinessPartnerStatus = async () => {
    let updatedModalData;

    if (modalData.type === 'Customer') {
      await fetchCompanyDetails();
      updatedModalData = getInvoiceCustomerModalDetails();
    }
    if (modalData.type === 'Consignee') {
      const initialMop = { ...consigneeStatus }.ffdMoP;
      const newStatus = await getBusinessPartnerDetails(consigneeData, 'consignee');
      setConsigneeStatus(newStatus);
      updatedModalData = getBusinessPartnerModalDetails('Consignee', consigneeData, consigneeStatus);

      const isAllVatZ = store.QAProductsList[0]?.every((item) => item.vat === VatTypeEnum.VATZ);
      const oneOrMoreVatS = store.QAProductsList[0]?.some((item) => item.vat === VatTypeEnum.VATS);
      const { hasFFD, hasGBImport, hasEUImportEntry } = qaStore.currentConfig[0].documents || {};

      const options = {
        customerAllowSAOnNeeNors: store.QACustomerData[0]?.allowSAOnNeeNors,
        isConsigneeBP: !!newStatus?.cabieStatus,
        vatTotal: qaStore.itemTotals[0]?.vatLiabilityGbp,
        isGBImport: hasGBImport || hasFFD,
        hasEUImportEntry,
        orderFromRepresentative: store.representative[0],
        isAllVatZ,
        oneOrMoreVatS,
        isPVA: store.QAConsigneeData[0]?.isPVA,
      };

      fetchRevenuePayableData(store.consignee[0], setNewLoadQaVat, options);

      if (initialMop !== newStatus.ffdMoP) {
        setCustomsMoPConfirmation({ confirmed: false, selection: null });
      }
    }
    if (modalData.type === 'Consignor') {
      const newStatus = await getBusinessPartnerDetails(consignorData, 'consignor', incoterm);
      setConsignorStatus(newStatus);
      updatedModalData = getBusinessPartnerModalDetails(
        'Consignor',
        consignorData,
        consignorStatus,
        showExWorksForConsignorBp
      );
    }

    updatedModalData && setModalData(updatedModalData);
  };

  const getInvoiceCustomerModalDetails = () => {
    const { id, cabieStatus, cabieStatusValid, dbStatus, dbStatusValid } = customerStatus;
    return {
      title: 'Customer Validation',
      type: 'Customer',
      isEditable: false,
      onView: () => handleViewClick(id),
      rows: [
        {
          title: 'invoice Customer',
          key: 'invoiceCustomer',
          value: companyName,
          hasError: false,
        },
        { title: 'CABIE Status', key: 'cabieStatus', value: cabieStatus, hasError: !cabieStatusValid },
        { title: 'DB Status', key: 'dbStatus', value: dbStatus, hasError: !dbStatusValid },
      ],
    };
  };

  const getBusinessPartnerModalDetails = (neenor, data, status, showExWorksRow = false) => {
    const { eori } = data;
    const {
      consignee: businessPartnerName,
      cabieStatus,
      cabieStatusValid,
      dbStatus,
      dbStatusValid,
      standingAuthority,
      standingAuthorityValid,
      exWorks,
      exWorksValid,
    } = status;

    const isBusinessPartner = !!status.id;

    const modalRows = [
      {
        title: neenor,
        key: 'companyName',
        value: businessPartnerName,
        hasError: false,
      },
      { title: 'EORI Number', key: 'eoriNumber', value: eori, readOnly: true },
      { title: 'CABIE Status', key: 'cabieStatus', value: cabieStatus, hasError: !cabieStatusValid },
      { title: 'DB Status', key: 'dbStatus', value: dbStatus, hasError: !dbStatusValid },
      {
        title: 'standingAuthority',
        key: 'standingAuthority',
        value: standingAuthority,
        hasError: !standingAuthorityValid,
      },
    ];

    if (showExWorksRow) {
      modalRows.push({ title: 'Ex Works Authorisation', key: 'dbStatus', value: exWorks, hasError: !exWorksValid });
    }

    return {
      title: `${neenor} Validation`,
      type: neenor,
      isEditable: false,
      onView: () => handleViewClick(status?.id),
      disableViewButton: !isBusinessPartner,
      rows: modalRows,
    };
  };

  const getNeeNorModalDetails = (neenor, data = {}) => {
    const { companyName, eori, building, street, town, postcode, country } = data;
    return {
      title: `${neenor} Validation`,
      heading: 'Address',
      isEditable: !readOnly,
      onSave: (updatedData) => saveNeenorData(data, updatedData, neenor),

      rows: [
        {
          title: neenor,
          key: 'companyName',
          value: companyName || '-',
          hasError: false,
        },
        { title: 'EORI Number', key: 'eoriNumber', value: eori, readOnly: true },
        { title: 'Building Name / Number', key: 'building', value: building },
        { title: 'Street Address', key: 'street', value: street },
        { title: 'City', key: 'town', value: town },
        { title: 'Postcode', key: 'postcode', value: postcode },
        { title: 'Country', key: 'country', value: country },
      ],
    };
  };

  const handleKebabClick = async (type) => {
    if (type === modalTypes.invoiceCustomer) {
      setModalData(getInvoiceCustomerModalDetails());
    } else if (type === modalTypes.consignee) {
      if (config.consigneeOptions.useBusinessPartners) {
        setModalData(getBusinessPartnerModalDetails('Consignee', consigneeData, consigneeStatus));
      } else {
        setModalData(getNeeNorModalDetails('Consignee', consigneeData));
      }
    } else if (type === modalTypes.consignor) {
      if (multipleConsignors) {
        if (isMultipleInvoicesValid || readOnly) {
          setShowMultipleConsignorsModal(true);
        } else {
          // Show warning if multiple consignors is on, but not all invoices filled in
          setShowMultipleConsignorsWarning(true);
        }
        return;
      }

      if (config.consignorOptions.useBusinessPartners) {
        setModalData(
          getBusinessPartnerModalDetails('Consignor', consignorData, consignorStatus, showExWorksForConsignorBp)
        );
      } else {
        setModalData(getNeeNorModalDetails('Consignor', consignorData));
      }
    }

    setShowKebabModal(true);
  };

  const handleViewClick = (id) => {
    setSelectedBusinessPartner(id);
  };

  const handleCloseBusinessPartnerModal = async () => {
    await refreshBusinessPartnerStatus();
    setSelectedBusinessPartner(null);
  };

  const handleBusinessPartnerSave = async () => {
    await refreshBusinessPartnerStatus();
  };

  const fetchCompanyDetails = async () => {
    try {
      const data = await getBusinessPartnerStatus(companyId);
      let customerData = { ...data, companyName: companyName };

      if (readOnly && persistedData.length) {
        const persistedCompanyData = formatCompliancePersistedData(persistedData[0], 'company', companyName);
        customerData = { ...customerData, ...persistedCompanyData };
      }

      setCustomerStatus(customerData);
    } catch {
      setCustomerStatus({
        billableCustomer: '',
        billableCustomerValid: true,
        cabieStatus: '',
        cabieStatusValid: false, //if unable to fetch data, cabieStatusValid is false
        cm8Approved: '',
        cm8ApprovedValid: true,
        companyName: companyName, //for invoice, companyName is passed as prop, so no need to populate from API
        dbStatus: '',
        dbStatusValid: false, //if unable to fetch data, dbStatusValid is false
        eori: '',
        id: null,
      });
    }
  };

  useEffect(() => {
    companyId && fetchCompanyDetails();
  }, [companyId, companyName, setCustomerStatus]);

  useEffect(() => {
    const refresh = async () => {
      if (consigneeData?.id) {
        const newStatus = await getBusinessPartnerDetails(consigneeData, 'consignee');
        setConsigneeStatus(newStatus);
      }
    };
    refresh();
  }, [consigneeData?.id, setConsigneeStatus, readOnly, persistedData]);

  useEffect(() => {
    const refresh = async () => {
      if (consignorData?.id) {
        const newStatus = await getBusinessPartnerDetails(consignorData, 'consignor', incoterm);
        setConsignorStatus(newStatus);
      }
    };
    refresh();
  }, [consignorData, setConsignorStatus, incoterm, readOnly, persistedData]);

  const invoiceCustomerError = !isInvoiceCustomerValid;
  const consigneeBlocked = !!consigneeData?.blocked;
  const consignorBlocked = !!consignorData?.blocked;
  const consigneeBpInvalid = config?.consigneeOptions?.useBusinessPartners && !isConsigneeValid;
  const consignorBpInvalid = config?.consignorOptions?.useBusinessPartners && !isConsignorBPValid;
  const customerAllowsSA = customerStatus.allowSAOnNeeNors && representative;
  const consigneeError = consigneeBlocked || (consigneeBpInvalid && !customerAllowsSA);
  const consignorError = consignorBlocked || (consignorBpInvalid && !customerAllowsSA);
  const multiplesError = multipleConsignors && !(isMultipleInvoicesValid && isMultipleConsignorsValid);
  const preferenceError = config?.visibleFields?.preference && !isPreferenceCheckValid;
  const typeError = config?.visibleFields?.type && !!typeErrorMessage;
  const isComplianceValid = !(
    invoiceCustomerError ||
    consigneeError ||
    consignorError ||
    multiplesError ||
    preferenceError ||
    typeError
  );

  useEffect(() => {
    setIsComplianceChecklistValid(isComplianceValid);
  }, [isComplianceValid, setIsComplianceChecklistValid]);

  const tableData = {
    headings: [
      {
        label: 'Invoice Customer',
        key: 'invoiceCustomer',
        truncate: true,
        error: invoiceCustomerError,
        onClick: () => handleKebabClick(modalTypes.invoiceCustomer),
        valueCellId: 'qa-compliance-invoice-customer-value',
        kebabMenuId: 'qa-compliance-invoice-customer-kebab',
      },
      {
        label: 'Consignor',
        key: 'consignor',
        truncate: true,
        error: consignorError || multiplesError,
        onClick: () => handleKebabClick(modalTypes.consignor),
        valueCellId: 'qa-compliance-consignor-value',
        kebabMenuId: 'qa-compliance-consignor-kebab',
        disabled: !qaStore.consignorData?.[0]?.id,
      },
      {
        label: 'Consignee',
        key: 'consignee',
        truncate: true,
        error: consigneeError,
        onClick: () => handleKebabClick(modalTypes.consignee),
        valueCellId: 'qa-compliance-consignee-value',
        kebabMenuId: 'qa-compliance-consignee-kebab',
        disabled: !qaStore.consigneeData?.[0]?.id,
      },
      {
        label: 'Preference',
        key: 'preference',
        error: preferenceError,
        hide: !config?.visibleFields?.preference,
        valueCellId: 'qa-compliance-preference-value',
      },
      {
        label: 'Type',
        key: 'type',
        hide: !config?.visibleFields?.type,
        errorMessage: typeErrorMessage,
        error: typeError,
        showErrorIcon: typeError,
        valueCellId: 'qa-compliance-type-value',
      },
    ],
    values: [
      {
        id: 'compliance-checklist-table-values',
        invoiceCustomer: companyName || '-',
        consignor: multipleConsignors ? 'Multiple Consignors' : consignorData?.companyName,
        consignee: consigneeData?.companyName,
        preference: displayCheckModal && (
          <button
            data-testid="qa-compliance-check-button"
            className="button-text-link"
            onClick={() => setShowPreferenceCheckModal(true)}
          >
            Check
          </button>
        ),
        type: controlledGoodsTypeValue,
      },
    ],
  };

  return (
    <>
      {/* initial modal */}
      <ComplianceChecklistModal
        show={showKebabModal}
        title={modalData.title}
        heading={modalData.heading}
        rows={modalData.rows}
        isEditable={modalData.isEditable}
        disableViewButton={modalData.disableViewButton}
        onSave={modalData.onSave}
        onView={modalData.onView}
        onClose={() => setShowKebabModal(false)}
      />

      {/* secondary business partner modal */}
      <Modal show={!!selectedBusinessPartner} onHide={handleCloseBusinessPartnerModal} size="xl" centered>
        <BusinessPartnerCard
          back={handleCloseBusinessPartnerModal}
          id={selectedBusinessPartner}
          onSaveCallback={handleBusinessPartnerSave}
        />
      </Modal>

      {/* multiple consignors modal */}
      <MultipleConsignorsModal
        show={showMultipleConsignorsModal}
        onHide={() => setShowMultipleConsignorsModal(false)}
        invoiceLines={invoiceLines}
        consignorDetails={consignorDetails}
        locked
      />

      <ConfirmActionModal
        title="Multiple Consignors"
        show={showMultipleConsignorsWarning}
        onConfirm={() => setShowMultipleConsignorsWarning(false)}
        onClose={() => setShowMultipleConsignorsWarning(false)}
        message="Please enter all invoice references."
        hideCancel
      />

      <SimpleTable data={tableData} isHeaderRow={false} />
    </>
  );
};

export default ComplianceChecklistTable;
